import React, { useState, useMemo } from 'react';
import { Fuel, Trash2, Droplets, Info, AlertCircle, FileText, Download, Bell } from 'lucide-react';
import { Badge } from '../../../catalyst/badge';
import BunkeringRecords from '../logbook/BunkeringRecords';

/**
 * Environmental Tab - Bunkers management and environmental compliance guidance
 * Updated 2024 - Provides guidance for MARPOL requirements without serving as official records
 */
const EnvironmentalTab = ({ currentVessel }) => {
  const [activeSection, setActiveSection] = useState('bunkers');
  
  // Check vessel requirements per 2024 MARPOL updates
  const grossTonnage = parseInt(currentVessel?.grossTonnage) || 0;
  const lengthOverall = parseInt(currentVessel?.lengthOverall) || 0;
  const personsCapacity = parseInt(currentVessel?.maxPersons || currentVessel?.personsCapacity) || 0;
  const isInternational = currentVessel?.voyageType === 'international';
  
  // Determine what compliance guidance to show based on 2024 requirements
  // Garbage: ≥100 GT (changed from 400 GT in May 2024) OR ≥15 persons OR ≥12m for placards
  const requiresGarbageRecords = grossTonnage >= 100 || (personsCapacity >= 15 && isInternational);
  const requiresGarbagePlan = grossTonnage >= 100 || personsCapacity >= 15;
  const requiresGarbagePlacards = lengthOverall >= 12;
  // Oil Record Book: Still ≥400 GT (MARPOL Annex I unchanged)
  const requiresOilRecordBook = grossTonnage >= 400;
  // Fuel samples: ≥400 GT OR any vessel taking fuel oil
  const requiresFuelSamples = grossTonnage >= 400 || currentVessel?.takesFuelOil;
  
  // Section configuration
  const sections = [
    { 
      id: 'bunkers', 
      label: 'Bunkers & Fuel', 
      icon: Fuel, 
      color: 'amber',
      description: 'Fuel management, BDNs, samples, consumption tracking',
      show: true 
    },
    { 
      id: 'compliance', 
      label: 'Compliance Guidance', 
      icon: FileText, 
      color: 'blue',
      description: 'Environmental compliance reminders and guidance',
      show: requiresGarbageRecords || requiresOilRecordBook || requiresGarbagePlan,
      requirement: null
    }
  ];
  
  const visibleSections = sections.filter(section => section.show);
  const activeConfig = sections.find(s => s.id === activeSection);
  
  return (
    <div className="space-y-4">
      {/* Sub-navigation */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">Environmental Records</h2>
            {currentVessel && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>{grossTonnage} GT</span>
                {lengthOverall > 0 && <span>• {lengthOverall}m LOA</span>}
                {personsCapacity > 0 && <span>• {personsCapacity} persons</span>}
              </div>
            )}
          </div>
        </div>
        
        {/* Section buttons */}
        <div className="p-4">
          <div className="flex flex-wrap gap-2">
            {visibleSections.map(section => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`
                    px-4 py-2 rounded-lg border flex items-center gap-2 text-sm font-medium
                    transition-colors
                    ${isActive 
                      ? 'bg-cyan-50 border-cyan-300 text-cyan-700' 
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }
                  `}
                >
                  <Icon className="h-4 w-4" />
                  <span>{section.label}</span>
                  {section.requirement && (
                    <Badge size="xs" color={isActive ? 'indigo' : 'gray'}>
                      {section.requirement}
                    </Badge>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Description */}
          {activeConfig && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-600">
                  {activeConfig.description}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Content Area */}
      <div>
        {activeSection === 'bunkers' && (
          <BunkeringRecords />
        )}
        
        {activeSection === 'compliance' && (
          <div className="space-y-4">
            {/* Garbage Record Book Guidance */}
            {requiresGarbageRecords && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start gap-3">
                  <Trash2 className="h-5 w-5 text-green-600 mt-1" />
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-gray-900 mb-2">
                      Garbage Record Book Requirement
                    </h3>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                        <p className="text-sm text-yellow-800">
                          This vessel requires an official Garbage Record Book Part I
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        <span><strong>Required for:</strong> Vessels ≥100 GT or ≥15 persons (international)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        <span><strong>Retention:</strong> 2 years from last entry</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        <span><strong>Categories:</strong> A-I per MARPOL Annex V</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        <span><strong>Reference:</strong> IMO Res. MEPC.360(79) - 2024 Update</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button className="px-3 py-1.5 text-sm bg-cyan-600 text-white rounded hover:bg-cyan-700 flex items-center gap-1">
                        <FileText className="h-4 w-4" />
                        View Entry Guide
                      </button>
                      <button className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1">
                        <Bell className="h-4 w-4" />
                        Set Reminders
                      </button>
                      <button className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1">
                        <Download className="h-4 w-4" />
                        Download Template
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Oil Record Book Guidance */}
            {requiresOilRecordBook && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-start gap-3">
                  <Droplets className="h-5 w-5 text-cyan-600 mt-1" />
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-gray-900 mb-2">
                      Oil Record Book Requirement
                    </h3>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                        <p className="text-sm text-yellow-800">
                          This vessel requires an official Oil Record Book Part I
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-2">
                        <span className="text-cyan-600">✓</span>
                        <span><strong>Required for:</strong> Vessels ≥400 GT</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-cyan-600">✓</span>
                        <span><strong>Retention:</strong> 3 years from last entry</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-cyan-600">✓</span>
                        <span><strong>Operations:</strong> Codes A-I per MARPOL Annex I</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-cyan-600">✓</span>
                        <span><strong>Reference:</strong> WBC3 Section 30.1 / MARPOL Annex I Reg.17</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button className="px-3 py-1.5 text-sm bg-cyan-600 text-white rounded hover:bg-cyan-700 flex items-center gap-1">
                        <FileText className="h-4 w-4" />
                        View Entry Guide
                      </button>
                      <button className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1">
                        <Bell className="h-4 w-4" />
                        Set Reminders
                      </button>
                      <button className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1">
                        <Download className="h-4 w-4" />
                        Download Template
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Requirements */}
            {(requiresGarbagePlacards || requiresGarbagePlan) && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-base font-medium text-gray-900 mb-3">Additional Environmental Requirements</h3>
                <div className="space-y-2">
                  {requiresGarbagePlacards && (
                    <div className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                      <Info className="h-4 w-4 text-gray-500 mt-0.5" />
                      <div className="text-sm">
                        <strong>Garbage Disposal Placards:</strong> Required (vessel ≥12m overall length)
                      </div>
                    </div>
                  )}
                  {requiresGarbagePlan && (
                    <div className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                      <Info className="h-4 w-4 text-gray-500 mt-0.5" />
                      <div className="text-sm">
                        <strong>Garbage Management Plan:</strong> Required (vessel ≥100 GT or ≥15 persons)
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Future Integration Notice */}
            <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-cyan-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <strong>Digital Record Book Integration</strong><br/>
                  Electronic record book functionality coming soon - pending MCA type-approval.
                  Currently, maintain official paper record books and use this system for compliance guidance.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnvironmentalTab;